import styled from '@emotion/styled';
import React from 'react';
import { connect } from 'react-redux';

import { AppState } from 'src/config/appState';
import { StepButtons, StepButtonsProps } from 'src/modules/programs/components/StepButtons';
import * as S from 'src/modules/programs/stepComponents/styles';
import stepIcon from 'src/public/img/undraw-to-do-list.svg';
import { CircularRates } from 'src/shared/components/CircularRates';
import { color, font, mediaQueries } from 'src/shared/styles/variables';

import {
    changeHomeworkConfidenceRatingValue,
    submitHomeworkConfidenceRatingRequest,
} from '../actions/weekTwoActions';
import {
    getHomeworkConfidenceRating,
    isHomeworkConfidenceRatingValid,
} from '../selectors/weekTwoSelectors';

const Heading = styled('h3')(
    {
        color: color.chathamsBlue,
        margin: '2.5rem 0 1rem',
    },
    font.heavy(font.size.mediumExtended),
);

const ActivityContent = styled('p')(
    {
        color: color.chathamsBlue,
        lineHeight: 1.63,
    },
    font.book(font.size.medium),
);

const ActivityContentParagraph = styled('p')(
    {
        color: color.chathamsBlue,
        lineHeight: 1.63,
        fontStyle: 'oblique',
        paddingTop: '2rem',
    },
    font.book(font.size.medium),
);

const RatingLabels = styled('div')({
    paddingTop: '1.5rem',
    display: 'flex',
    justifyContent: 'space-between',
    [mediaQueries.sm]: {
        display: 'none',
    },
});

const RatingLabel = styled('span')(
    {
        display: 'inline-block',
        color: color.scooter,
    },
    font.medium(font.size.medium),
);

interface StateProps {
    value: number;
    isHomeworkConfidenceRatingValid: boolean;
}

interface ActionsProps {
    changeHomeworkConfidenceRatingValue: typeof changeHomeworkConfidenceRatingValue;
    submitHomeworkConfidenceRatingRequest: typeof submitHomeworkConfidenceRatingRequest;
}

type Props = StepButtonsProps & StateProps & ActionsProps;

export class WeekTwoPlanStepContainer extends React.Component<Props> {
    private handleRateChange = (value: number) =>
        this.props.changeHomeworkConfidenceRatingValue(value);

    private handleSubmit = () => {
        this.props.submitHomeworkConfidenceRatingRequest(this.props.value);

        if (this.props.isHomeworkConfidenceRatingValid) {
            this.props.goToNextStep();
        }
    };

    public render(): JSX.Element {
        return (
            <>
                <S.ContentRow>
                    <S.ContentColumn>
                        <S.StepHeading small>Homework:</S.StepHeading>
                        <S.StepHeading>Over the next week...</S.StepHeading>
                    </S.ContentColumn>
                </S.ContentRow>
                <S.ContentRow>
                    <S.ContentColumn md={14}>
                        <S.PlannedActivitiesHolder>
                            <S.PlannedActivitiesHeading>
                                Planned activities:
                            </S.PlannedActivitiesHeading>
                            <S.PlannedActivity>
                                <ActivityContent>
                                    Notice and write down times in the week when you feel like you
                                    ARE and ARE NOT living according to your values. Where are you?
                                    What are you doing? Who are you with? Noticing these moments can
                                    give us some great clues as to what it is we really care about.
                                </ActivityContent>
                                <ActivityContentParagraph>
                                    Don't worry if you notice that you aren't living according to
                                    your values. Next week we will be learning how to do that more!
                                </ActivityContentParagraph>
                            </S.PlannedActivity>
                            <S.PlannedActivity>
                                <ActivityContent>
                                    Continue practicing mindfulness. Track your progress here.
                                </ActivityContent>
                            </S.PlannedActivity>
                        </S.PlannedActivitiesHolder>
                        <Heading>
                            How confident are you that you can practice the homework this week?
                        </Heading>
                        <CircularRates
                            range={10}
                            currentRate={this.props.value}
                            onRateChange={this.handleRateChange}
                            fullWidth
                        />
                        <RatingLabels>
                            <RatingLabel>Not at all confident</RatingLabel>
                            <RatingLabel>Totally confident</RatingLabel>
                        </RatingLabels>
                    </S.ContentColumn>
                    <S.ContentColumn md={10}>
                        <S.StepIcon iconUrl={stepIcon} />
                    </S.ContentColumn>
                </S.ContentRow>
                <StepButtons
                    onPreviousClick={this.props.goToPreviousStep}
                    onNextClick={this.handleSubmit}
                    nextDisabled={!this.props.isHomeworkConfidenceRatingValid}
                    hasNextStep
                />
            </>
        );
    }
}

const mapStateToProps = (state: AppState): StateProps => ({
    value: getHomeworkConfidenceRating(state),
    isHomeworkConfidenceRatingValid: isHomeworkConfidenceRatingValid(state),
});

export const WeekTwoPlanStep = connect<StateProps, ActionsProps>(mapStateToProps, {
    changeHomeworkConfidenceRatingValue,
    submitHomeworkConfidenceRatingRequest,
})(WeekTwoPlanStepContainer);
